/**
 * Single source of truth for the portal's "mobile vs desktop" split.
 *
 * Before this file, components picked their own number: some tested
 * `window.innerWidth < 768`, one read `(max-width: 767px)` via `matchMedia`,
 * and the stylesheets used `@media (max-width: 767px)`. At a fractional
 * viewport width (browser zoom, some device pixel ratios) `innerWidth < 768`
 * and `(max-width: 767px)` disagree, so the same page could paint its desktop
 * CSS while its TypeScript believed it was on mobile.
 *
 * The CSS breakpoint is authoritative: everything here is derived from
 * `MOBILE_MAX_WIDTH_PX` so TS and SCSS can never drift apart. The SCSS side of
 * the same constant lives in `src/styles/_breakpoints.scss`.
 */

/** Widest viewport still considered "mobile". Mirrors `$mobile-max-width`. */
export const MOBILE_MAX_WIDTH_PX = 767;

/** Media query string matching `MOBILE_MAX_WIDTH_PX`. */
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH_PX}px)`;

/** Builds a `max-width` media query for a custom breakpoint. */
export function maxWidthQuery(maxWidthPx: number): string {
  return `(max-width: ${maxWidthPx}px)`;
}

/**
 * Whether the current viewport is at or below `maxWidthPx`.
 *
 * Prefers `matchMedia` so the answer is exactly what the stylesheet sees;
 * falls back to `innerWidth` for environments without it (older test shims,
 * SSR-ish contexts). Returns `false` when there is no window at all, which
 * keeps server-side/unit-test rendering on the desktop layout.
 */
export function isMobileViewport(maxWidthPx: number = MOBILE_MAX_WIDTH_PX): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(maxWidthQuery(maxWidthPx)).matches;
  }

  if (typeof window.innerWidth === 'number') {
    return window.innerWidth <= maxWidthPx;
  }

  return false;
}
